/**
 * Composition libre : le panneau.
 *
 * Le parcours et les décisions sont dans l'en-tête de
 * blocks/product-pack-composer.liquid. Ici : tenir le compte des pièces par
 * coloris, afficher ce que la quantité débloque, et confier la ligne au panier.
 *
 * L'ajout lui-même n'est pas fait ici : le panneau émet `pack:add`, et c'est
 * assets/pack-cart.js qui envoie la ligne, avec la même mécanique que pour les
 * packs. Un seul endroit parle au panier.
 */

const normalise = (value) => (value || '').toString().trim().toLowerCase();

/**
 * Lignes de panier d'une composition, et si elles sont séparées par coloris.
 *
 * Le Pack perso est décliné en Coloris × Taille : une ligne par coloris, à la
 * quantité de ce coloris. DSers relie une variante Shopify à une variante
 * AliExpress et ne lit pas les propriétés de ligne : seule une variante par
 * coloris lui fait commander les bons articles. Décision du principal,
 * 2026-09-11.
 *
 * Tant que le produit n'est décliné qu'en taille, une seule ligne part, et les
 * coloris ne sont écrits que dans « Contenu ». Un coloris introuvable sur un
 * Pack perso décliné en coloris est une erreur, jamais un repli : la pièce
 * partirait dans un autre coloris.
 *
 * @param {{ options: string[], variants: { id: number, available: boolean, options: string[] }[] }} map
 * @param {string} size
 * @param {{ name: string, quantity: number }[]} picked
 * @returns {{ split: boolean, lines: { id: number, available: boolean, quantity: number }[] } | null}
 */
function resolveLines(map, size, picked) {
  /** @param {{ options: string[] }} variant @param {string} value */
  const has = (variant, value) => variant.options.some((option) => normalise(option) === normalise(value));
  const sized = map.variants.filter((variant) => has(variant, size));
  if (!sized.length || !picked.length) return null;

  if (map.options.length < 2) {
    const [variant] = sized;
    const quantity = picked.reduce((total, colour) => total + colour.quantity, 0);
    return { split: false, lines: [{ id: variant.id, available: variant.available, quantity }] };
  }

  const lines = [];
  for (const colour of picked) {
    const variant = sized.find((candidate) => has(candidate, colour.name));
    if (!variant) return null;
    lines.push({ id: variant.id, available: variant.available, quantity: colour.quantity });
  }
  return { split: true, lines };
}

/**
 * « 1:34,99 | 3:29,99 » → [{ from: 1, cents: 3499 }, { from: 3, cents: 2999 }]
 * @param {string} raw
 * @returns {{ from: number, cents: number }[]}
 */
function parseGrid(raw) {
  return (raw || '')
    .split('|')
    .map((part) => {
      const [count, price] = part.split(':').map((piece) => piece.trim());
      const from = parseInt(count ?? '', 10);
      const cents = Math.round(parseFloat((price ?? '').replace(/\s/g, '').replace(',', '.')) * 100);
      return { from, cents };
    })
    .filter((step) => Number.isFinite(step.from) && step.from > 0 && Number.isFinite(step.cents) && step.cents > 0)
    .sort((a, b) => a.from - b.from);
}

/** @param {string | undefined} value @param {number} fallback */
const toInt = (value, fallback) => {
  const n = parseInt(value ?? '', 10);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const money = new Intl.NumberFormat('fr-FR', {
  style: 'currency',
  currency: window.Shopify?.currency?.active || 'EUR',
});

/** @param {number} cents */
const formatMoney = (cents) => money.format(cents / 100);

class PackComposer extends HTMLElement {
  connectedCallback() {
    /** @type {{ name: string, image: string }[]} */
    this.colours = [...this.querySelectorAll('[data-colour]')]
      .filter((el) => el instanceof HTMLElement)
      .map((el) => ({ name: el.dataset.colour || '', image: el.dataset.image || '' }))
      .filter((colour) => colour.name);

    /** @type {Map<string, number>} */
    this.counts = new Map(this.colours.map((colour) => [colour.name, 0]));

    this.grid = parseGrid(this.dataset.priceGrid || '');
    this.min = toInt(this.dataset.min, 2);
    this.max = toInt(this.dataset.max, 10);
    this.shippingFrom = toInt(this.dataset.shippingFrom, 2);
    /** Taille retenue dans le panneau, valeur exacte de l'option du Pack perso. */
    this.size = '';
    /** @type {'size' | 'colours'} */
    this.panel = 'size';
    this.busy = false;

    this.addEventListener('click', this.handleClick);
    this.addEventListener('size-finder:apply', this.handleFinderApply);
    this.addEventListener('size-finder:exit', this.handleFinderExit);
    document.addEventListener('click', this.handleOpen);

    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('size-finder:apply', this.handleFinderApply);
    this.removeEventListener('size-finder:exit', this.handleFinderExit);
    document.removeEventListener('click', this.handleOpen);
  }

  /** Les tuiles d'ouverture vivent dans d'autres blocs : écoute déléguée. */
  handleOpen = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('[data-pack-compose]')) return;
    this.open();
  };

  open() {
    // Taille et coloris sont gardés d'une ouverture à l'autre, jusqu'à l'ajout
    // au panier : refermer pour vérifier quelque chose ne doit rien coûter.
    // Sans taille retenue, on repart du volet Taille.
    if (!this.size) this.showPanel('size', false);

    // Le gabarit du définisseur vit dans le sélecteur de variante, rendu après
    // ce bloc : il ne se cherche qu'ici, pas à la connexion.
    const links = this.querySelector('[data-finder-links]');
    if (links instanceof HTMLElement) {
      links.hidden = !document.querySelector('template[data-size-finder-template]');
    }

    this.showError('');
    this.render();
    this.closest('dialog-component')?.showDialog?.();
  }

  /** @returns {HTMLButtonElement[]} */
  get sizeButtons() {
    return [...this.querySelectorAll('[data-action="size"]')].filter(
      (button) => button instanceof HTMLButtonElement
    );
  }

  get filled() {
    let sum = 0;
    for (const n of this.counts.values()) sum += n;
    return sum;
  }

  /** Prix par pièce, en centimes, pour un nombre de pièces donné. */
  unitFor(count) {
    let cents = this.grid[0]?.cents ?? 0;
    for (const step of this.grid) {
      if (count >= step.from) cents = step.cents;
    }
    return cents;
  }

  handleClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-action]');
    if (!(trigger instanceof HTMLButtonElement) || trigger.disabled || this.busy) return;

    const colour = trigger.dataset.colour || trigger.closest('[data-colour]')?.getAttribute('data-colour') || '';

    switch (trigger.dataset.action) {
      case 'add':
        this.step(colour, 1);
        break;
      case 'remove':
        // 🛑 Le récap est reconstruit par `render`, et le « − » cliqué sort du
        // document avant que le clic n'atteigne dialog-component. Celui-ci
        // teste `contains(event.target)` pour repérer un clic sur le fond :
        // sur un nœud détaché, le test échoue et le panneau se refermait à
        // chaque retrait. Le clic est entièrement traité ici, il s'arrête ici.
        event.stopPropagation();
        this.step(colour, -1);
        break;
      case 'add-to-cart':
        this.addToCart();
        break;
      case 'return':
        // Un cran en arrière à chaque fois : des coloris vers la taille, du
        // définisseur ou de la grille vers la rangée de tailles, puis dehors.
        if (this.panel === 'colours') this.showPanel('size');
        else if (this.finderOpen) this.hideFinder();
        else this.closest('dialog-component')?.closeDialog?.();
        break;
      case 'size':
        this.size = trigger.dataset.size || '';
        this.showError('');
        this.render();
        break;
      case 'continue':
        if (this.size) this.showPanel('colours');
        break;
      case 'finder':
        this.showFinder('finder');
        break;
      case 'show-grid':
        this.showFinder('grid');
        break;
    }
  };

  /**
   * @param {'size' | 'colours'} name
   * @param {boolean} [focus]
   */
  showPanel(name, focus = true) {
    this.panel = name;

    for (const panel of this.querySelectorAll('[data-panel]')) {
      if (panel instanceof HTMLElement) panel.hidden = panel.dataset.panel !== name;
    }

    // Tout changement de volet referme le définisseur. De retour sur le volet
    // Taille, c'est la rangée qu'on retrouve ; en passant aux coloris, le
    // titre et les petites tailles, qu'il masque, doivent revenir.
    this.hideFinder(false);

    this.closest('dialog')?.scrollTo({ top: 0 });
    this.render();

    if (!focus) return;

    // Le focus suit le volet : resté sur un bouton masqué, il serait perdu.
    const target =
      name === 'size'
        ? this.querySelector('[data-panel="size"] [aria-pressed="true"]') ??
          this.querySelector('[data-panel="size"] [data-action="size"]:not(:disabled)')
        : this.querySelector('[data-panel="colours"] [data-action="add"]');
    if (target instanceof HTMLElement) target.focus();
  }

  /**
   * Définisseur ou grille seule, dans le volet Taille. Le définisseur est cloné
   * au premier appel depuis le gabarit de la fiche (snippets/variant-size-guide).
   * @param {'finder' | 'grid'} mode
   */
  showFinder(mode) {
    const container = this.querySelector('[data-finder]');
    if (!(container instanceof HTMLElement)) return;

    if (!container.firstElementChild) {
      const template = document.querySelector('template[data-size-finder-template]');
      if (!(template instanceof HTMLTemplateElement)) return;

      const fragment = template.content.cloneNode(true);
      const finder = fragment instanceof DocumentFragment ? fragment.querySelector('size-finder') : null;
      if (finder instanceof HTMLElement) {
        // Sa première question se quitte par son propre « Retour » : la barre
        // du panneau est masquée tant qu'il est ouvert.
        finder.setAttribute('data-exitable', '');

        // Son dernier bouton mène au composeur plutôt que de cocher la fiche.
        const apply = finder.querySelector('[data-action="apply"]');
        if (apply instanceof HTMLElement && container.dataset.applyLabel) {
          apply.dataset.label = container.dataset.applyLabel;
        }
      }

      container.append(fragment);
    }

    container.hidden = false;
    container.dataset.mode = mode;
    // Définisseur ou grille ouverts, ils occupent seuls le panneau, titre
    // compris : le définisseur est déjà long. « Retour » fait revenir le volet.
    this.toggleAttribute('data-finder-open', true);

    if (mode === 'grid') {
      // La grille ne montre aucune étape, et son « Retour » est celui de la
      // première : on y ramène le définisseur, réponses gardées. Laissé sur
      // une étape plus loin, « Retour » y reculait sans sortir.
      const finder = container.querySelector('size-finder');
      if (finder && 'showStep' in finder && typeof finder.showStep === 'function') finder.showStep(0);

      const grid = container.querySelector('[data-grid]');
      if (grid instanceof HTMLDetailsElement) {
        grid.hidden = false;
        grid.open = true;
      }
    }

    container.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  get finderOpen() {
    return this.hasAttribute('data-finder-open');
  }

  /** Referme définisseur ou grille, et rend la rangée de tailles. */
  hideFinder(focus = true) {
    const container = this.querySelector('[data-finder]');
    if (container instanceof HTMLElement) container.hidden = true;
    this.removeAttribute('data-finder-open');

    if (!focus) return;
    const target =
      this.querySelector('[data-panel="size"] [aria-pressed="true"]') ??
      this.querySelector('[data-panel="size"] [data-action="size"]:not(:disabled)');
    if (target instanceof HTMLElement) target.focus();
  }

  /** « Retour » à la première question du définisseur : la rangée revient. */
  handleFinderExit = () => {
    this.hideFinder();
  };

  /** La recommandation du définisseur, retenue ici : la fiche n'est pas touchée. */
  handleFinderApply = (event) => {
    if (!(event instanceof CustomEvent)) return;
    event.preventDefault();

    const size = event.detail?.size;
    const button = this.sizeButtons.find((candidate) => candidate.dataset.size === size);

    // Épuisée dans le Pack perso alors que la fiche l'a encore : on referme le
    // définisseur et on reste sur le volet, où la taille apparaît barrée.
    if (!button || button.disabled) {
      this.hideFinder();
      this.render();
      return;
    }

    this.size = size;
    this.showPanel('colours');
  };

  /**
   * @param {string} colour
   * @param {number} delta
   */
  step(colour, delta) {
    if (!this.counts.has(colour)) return;

    const next = (this.counts.get(colour) || 0) + delta;
    if (next < 0 || (delta > 0 && this.filled >= this.max)) return;

    this.counts.set(colour, next);
    this.showError('');
    this.render();

    // Le récap perd sa vignette au dernier « − » : le focus y serait perdu avec
    // elle. On le rend au « + » du même coloris.
    if (delta < 0 && next === 0) {
      const pick = this.querySelector(`[data-colour="${CSS.escape(colour)}"] [data-action="add"]`);
      if (pick instanceof HTMLElement) pick.focus();
    }
  }

  render() {
    const filled = this.filled;
    const base = this.grid[0]?.cents ?? 0;
    const unit = this.unitFor(Math.max(filled, 1));
    const total = unit * filled;
    const saving = base * filled - total;
    const last = this.grid[this.grid.length - 1];
    const full = filled >= this.max;

    for (const button of this.sizeButtons) {
      button.setAttribute('aria-pressed', String(button.dataset.size === this.size));
    }

    const proceed = this.querySelector('[data-action="continue"]');
    if (proceed instanceof HTMLButtonElement) {
      proceed.disabled = !this.size;
      proceed.textContent = (this.size ? proceed.dataset.label : proceed.dataset.labelEmpty) || '';
    }

    for (const el of this.querySelectorAll('[data-colour]')) {
      if (!(el instanceof HTMLElement) || el.tagName === 'BUTTON') continue;
      const n = this.counts.get(el.dataset.colour || '') || 0;

      el.toggleAttribute('data-picked', n > 0);

      const badge = el.querySelector('[data-count-badge]');
      if (badge instanceof HTMLElement) {
        badge.textContent = String(n);
        badge.hidden = n === 0;
      }

      const pick = el.querySelector('[data-action="add"]');
      if (pick instanceof HTMLButtonElement) pick.disabled = full;
    }

    this.fillTemplate('[data-count-label]', { n: String(filled) });

    // L'avantage débloqué, dispositif Fincut. La livraison d'abord, l'économie
    // ensuite, en montant et non en pourcentage.
    const perk = this.querySelector('[data-perk]');
    if (perk instanceof HTMLElement) {
      const parts = [];
      if (filled >= this.shippingFrom && this.dataset.shippingLabel) parts.push(this.dataset.shippingLabel);
      if (saving > 0) parts.push(`${formatMoney(saving)} d'économie`);
      const text = parts.join(' et ');
      perk.textContent = text;
      perk.hidden = text === '';
    }

    const bar = this.querySelector('[data-composer-progress]');
    if (bar instanceof HTMLElement && last) {
      bar.style.setProperty('--progress', `${Math.min(filled / last.from, 1) * 100}%`);
    }

    this.fillTemplate('[data-unit-price]', { price: formatMoney(unit) });
    this.fillTemplate('[data-best-price]', { price: last ? formatMoney(last.cents) : '' });

    this.renderPicked();
    this.renderSubmit(filled, total, saving);
  }

  /** La sélection récapitulée, une vignette par coloris retenu, avec un « − ». */
  renderPicked() {
    const list = this.querySelector('[data-picked-list]');
    if (!list) return;

    const picked = this.colours.filter((colour) => (this.counts.get(colour.name) || 0) > 0);

    list.replaceChildren(
      ...picked.map((colour) => {
        const item = document.createElement('li');
        item.className = 'pack-composer__picked-item';

        const thumb = document.createElement('span');
        thumb.className = 'pack-composer__thumb';

        if (colour.image) {
          const image = document.createElement('img');
          image.className = 'pack-composer__thumb-image';
          image.src = colour.image;
          image.alt = '';
          thumb.append(image);
        }

        const badge = document.createElement('span');
        badge.className = 'pack-composer__badge';
        badge.setAttribute('aria-hidden', 'true');
        badge.textContent = String(this.counts.get(colour.name));

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'pack-composer__minus';
        remove.dataset.action = 'remove';
        remove.dataset.colour = colour.name;
        remove.setAttribute('aria-label', `Retirer un débardeur ${colour.name}`);
        remove.textContent = '−';

        thumb.append(badge, remove);

        const name = document.createElement('span');
        name.textContent = colour.name;

        item.append(thumb, name);
        return item;
      })
    );

    const title = this.querySelector('[data-picked-title]');
    if (title instanceof HTMLElement) title.hidden = picked.length === 0;
  }

  /**
   * @param {number} filled
   * @param {number} total - centimes
   * @param {number} saving - centimes
   */
  renderSubmit(filled, total, saving) {
    const submit = this.querySelector('[data-action="add-to-cart"]');
    if (!(submit instanceof HTMLButtonElement)) return;

    const ready = filled >= this.min;
    submit.disabled = !ready || this.busy;

    const label = submit.querySelector('[data-submit-label]');
    const price = submit.querySelector('[data-submit-price]');
    const compare = submit.querySelector('[data-submit-compare]');

    if (label) {
      label.textContent = ready
        ? submit.dataset.label || ''
        : (submit.dataset.labelMin || '').replace('{n}', String(this.min));
    }
    if (price) price.textContent = ready ? formatMoney(total) : '';
    if (compare) compare.textContent = ready && saving > 0 ? formatMoney(total + saving) : '';
  }

  /**
   * Le volet panier, ouvert une fois le panneau refermé : deux fenêtres
   * modales empilées se disputeraient le focus. Demande du principal,
   * 2026-09-11. Le réglage du thème « ouvrir le panier à l'ajout » est activé
   * depuis, pour les autres paliers ; il attend lui aussi la fermeture du
   * panneau, l'ajout partant de sa fenêtre. L'appel reste pour que la
   * composition ouvre le volet même si ce réglage est recoupé. Sans volet
   * (panier en page), rien.
   */
  openCartDrawer() {
    const drawer = document.querySelector('cart-drawer-component')?.closest('theme-drawer');
    if (!drawer || !('open' in drawer) || typeof drawer.open !== 'function') return;
    if ('isOpen' in drawer && drawer.isOpen) return;
    drawer.open();
  }

  /**
   * @param {string} selector
   * @param {Record<string, string>} values
   */
  fillTemplate(selector, values) {
    const el = this.querySelector(selector);
    if (!(el instanceof HTMLElement)) return;

    let text = el.dataset.template || '';
    for (const [key, value] of Object.entries(values)) text = text.replace(`{${key}}`, value);
    el.textContent = text;
  }

  /** @param {string} message */
  showError(message) {
    const error = this.querySelector('[data-error]');
    if (!(error instanceof HTMLElement)) return;
    error.textContent = message;
    error.hidden = message === '';
  }

  /** @returns {{ options: string[], variants: { id: number, available: boolean, options: string[] }[] } | null} */
  readPackMap() {
    const node = this.querySelector('[data-pack-perso-map]');
    if (!node?.textContent) return null;

    try {
      return JSON.parse(node.textContent);
    } catch {
      return null;
    }
  }

  async addToCart() {
    const filled = this.filled;
    if (filled < this.min || this.busy) return;

    const error = this.querySelector('[data-error]');
    const map = this.readPackMap();

    // Dans l'ordre des coloris de l'admin, quel que soit l'ordre des clics :
    // deux compositions identiques doivent s'écrire pareil au panier.
    const picked = this.colours
      .map((colour) => ({ name: colour.name, quantity: this.counts.get(colour.name) || 0 }))
      .filter((colour) => colour.quantity > 0);
    const resolved = map ? resolveLines(map, this.size, picked) : null;

    if (!resolved) {
      this.showError(error instanceof HTMLElement ? error.dataset.labelMissing || '' : '');
      return;
    }

    if (resolved.lines.some((line) => !line.available)) {
      this.showError(error instanceof HTMLElement ? error.dataset.labelUnavailable || '' : '');
      return;
    }

    const contents = picked.map((colour) => `${colour.name}:${colour.quantity}`).join(', ');
    const lines = resolved.lines.map(({ id, quantity }) => ({ id, quantity }));

    this.busy = true;
    this.render();
    this.setAttribute('aria-busy', 'true');

    try {
      await new Promise((resolve, reject) => {
        const detail = { lines, split: resolved.split, contents, resolve, reject, handled: false };
        // Émis depuis le panneau et non depuis le document : pack-cart.js en
        // fait la source de l'événement de panier, et le thème sait alors que
        // l'ajout vient d'une fenêtre modale encore ouverte.
        this.dispatchEvent(new CustomEvent('pack:add', { detail, bubbles: true }));
        // pack-cart.js est chargé par le bloc des paliers. Sans lui, personne ne
        // répond : mieux vaut le dire que laisser le bouton tourner.
        if (!detail.handled) reject(new Error(error instanceof HTMLElement ? error.dataset.labelMissing || '' : ''));
      });

      for (const colour of this.colours) this.counts.set(colour.name, 0);
      // Un nouveau pack repart de zéro, taille comprise : elle se choisit.
      this.size = '';
      this.showPanel('size', false);
      await this.closest('dialog-component')?.closeDialog?.();
      this.openCartDrawer();
    } catch (failure) {
      this.showError(failure instanceof Error ? failure.message : '');
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      this.render();
    }
  }
}

if (!customElements.get('pack-composer')) customElements.define('pack-composer', PackComposer);
